import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, Card, CardContent, Divider, FormControl, FormGroup, IconButton, Stack, TextField, Typography } from "@mui/material";
import { DataGrid, GridCallbackDetails, GridColDef, GridRowSelectionModel, useGridApiRef } from "@mui/x-data-grid";
import { useCallback, useEffect, useState } from "react";
import { evaluate } from "../../utils/evaluator";
import { dayjsUtcFormat } from "../../utils/util";
class ExampleRow {
  desc!: string;
  expr!: string;
  link?: string;

  public constructor(init?: Partial<ExampleRow>) {
    Object.assign(this, init);
  }
}

const LINKS = {
  DAYJS: "https://day.js.org/docs/en/parse/string-format",
  SPRINTFJS: "https://github.com/alexei/sprintf.js/#format-specification",
  EXPR: "https://github.com/silentmatt/expr-eval#expression-syntax",
}

export const TokensTabPage = () => {
  const init = {
    tokens: {
      date: new Date(),
    },
  };
  const apiRef = useGridApiRef();
  const [tokens, setTokens] = useState<any>(init.tokens);
  const [expression, setExpression] = useState("");
  const [result, setResult] = useState("");

  const randInt = (max: number) => Math.floor(Math.random() * max);

  const getNewTokens = () => {
    return {
      date: new Date(),
      SKU: `SKU${randInt(999).toString().padStart(5, "0")}`,
      Quantity: randInt(100),
      Discontinued: randInt(2) === 0,
      Decimal: Math.random() * 1000,
    }
  };

  const generateTokens = useCallback(() => {
    const tokens = getNewTokens();
    setTokens(tokens);
  }, []);

  const onRefresh = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const tokens = getNewTokens();
    setTokens(tokens);
    setResult(evaluate(expression, tokens) || "<empty>");
  }, [expression, tokens]);

  const tokenValue = (key: string) => {
    const value = tokens[key];
    if (value instanceof Date) {
      return dayjsUtcFormat(value);
    }
    return `${value}`;
  };

  const onExpressionChange = (value: string) => {
    setExpression(value);
    setResult(evaluate(value, tokens) || "<empty>");
  };

  const exampleColumns: GridColDef<ExampleRow>[] = [
    { field: "desc", headerName: "Description", minWidth: 100, flex: 200, editable: false, sortable: false, filterable: false, align: "left", headerAlign: "left" },
    { field: "expr", headerName: "Expression", minWidth: 100, flex: 400, editable: false, sortable: false, filterable: false, align: "left", headerAlign: "left" }, // p.row.retailer?.name || ""
    { field: "link", headerName: "Docs", width: 100, editable: false, sortable: false, filterable: false, align: "left", headerAlign: "left", renderCell: (p) => p.row.link ? (<a href={p.row.link} target="_blank">link</a>) : ("")},
  ];
  const exampleRows: ExampleRow[] = [
    { desc: "Simple token replace", expr: "SKU: {{SKU}}, Quantity: {{Quantity}}, Discontinued: {{Discontinued}}" },
    { desc: "Date without format", expr: "{{date}}", link: LINKS.DAYJS },
    { desc: "Date with format", expr: "{{date:YYYY-MM-DD HH:mm:ss}}", link: LINKS.DAYJS },
    { desc: "Quantity padded with spaces", expr: "{{Quantity:%6i}}", link: LINKS.SPRINTFJS },
    { desc: "Quantity padded with zeroes", expr: "{{Quantity:%06i}}", link: LINKS.SPRINTFJS },
    { desc: "Decimal without decimals", expr: "{{Decimal:%i}}", link: LINKS.SPRINTFJS },
    { desc: "Decimal with 2 decimals", expr: "{{Decimal:%.2f}}", link: LINKS.SPRINTFJS },
    { desc: "Decimal padded with spaces", expr: "{{Decimal:%10.2f}}", link: LINKS.SPRINTFJS },
    { desc: "Decimal padded with zeroes", expr: "{{Decimal:%010.2f}}", link: LINKS.SPRINTFJS },
    { desc: "Addition", expr: "{{%Quantity + 1}}", link: LINKS.EXPR },
    { desc: "Addition with format", expr: "{{%Quantity + 1:%05i}}", link: LINKS.EXPR },
    { desc: "Ternary conditional", expr: `{{%(Discontinued ? 1 : 0)}}`, link: LINKS.EXPR },
    { desc: "Ternary conditional with format", expr: `{{%(Discontinued ? "Yes" : "No"):%5s}}`, link: LINKS.EXPR },
    { desc: "If condition", expr: `{{%if(Discontinued, "Yes", "No")}}`, link: LINKS.EXPR },
    { desc: "If condition with format", expr: `{{%if(Discontinued, "Yes", "No"):%5s}}`, link: LINKS.EXPR },
    { desc: "Logical condition", expr:  `{{%(Quantity > 50 or Discontinued ? 1 : 0)}}`, link: LINKS.EXPR },
  ];

  const getSelectedRow = (): ExampleRow | null => {
    const rows = apiRef.current.getSelectedRows();
    if (rows && rows.size) {
      const [rowId, row] = rows.entries().next().value;
      return row as ExampleRow;
    }
    return null;
  };

  const handleRowSelectionModelChange = (model: GridRowSelectionModel, details: GridCallbackDetails) => {
    const row = getSelectedRow();
    if (row) onExpressionChange(row.expr);
  };

  useEffect(() => {
    generateTokens();
  }, [generateTokens]);

  return (
    <Box>
      <Box sx={{ width: 300 }}>
        <Card variant="outlined">
          <CardContent>
            <Typography sx={{ fontSize: 18 }} color="text.primary">
              Available tokens
            </Typography>
            <Typography component={"span"} sx={{ display: "flex", flexDirection: "column" }} color="text.secondary">
              {Object.keys(tokens).map((key, index) => (
                <Box sx={{ display: "flex", flexDirection: "row" }} key={index}>
                  <Box sx={{ width: 120, textAlign: "right", paddingRight: 2 }}>{key}</Box>
                  <Box>{tokenValue(key)}</Box>
                </Box>
              ))}
              <Stack spacing={2} direction="row" justifyContent="flex-end">
                <IconButton onClick={onRefresh}>
                  <RefreshIcon />
                </IconButton>
              </Stack>
              <Box sx={{ display: "flex", flexDirection: "row" }} alignItems="end"></Box>
            </Typography>
          </CardContent>
        </Card>
      </Box>
      <Divider sx={{ paddingTop: 10 }} />
      <br />
      <Box maxWidth="600px" sx={{ border: 1, borderRadius: "16px", p: 1 }}>
        <FormGroup>
          <FormControl component="fieldset" sx={{ minWidth: 150 }}>
            {/* <FormLabel component="legend">Expression evaluator</FormLabel> */}
            <FormGroup>
              <TextField label="Expression" variant="standard" value={expression} onChange={(e) => onExpressionChange(e.target.value)} />
              <br />
              <TextField label="Result" variant="standard" value={result} InputProps={{ readOnly: true }} />
            </FormGroup>
          </FormControl>
        </FormGroup>
      </Box>
      <br />
      <DataGrid sx={{ height: 300, width: 800 }}
        apiRef={apiRef}
        rows={exampleRows}
        rowCount={exampleRows.length}
        columns={exampleColumns} 
        rowHeight={30}
        hideFooter
        getRowId={(row: ExampleRow) => row.desc } 
        onRowSelectionModelChange={handleRowSelectionModelChange}
      />
    </Box>
  );
};
